import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { brandPath, fieldKey, stripProposals, type LandingSection } from '@brands/shared';
import { useAuth } from '../auth/AuthContext';
import type { Asset } from '../data/campaign';
import { useCampaign } from '../data/campaign';
import {
  isNewSince,
  useDeliverables,
  useLastVisit,
  useLandingContent,
} from '../data/clientCampaign';
import { Lightbox } from '../components/Lightbox';
import { Loading } from '../components/Loading';
import { useLazyAssetUrl } from '../lib/assetUrls';
import { callables } from '../lib/callables';
import { callableMessage, formatBytes, formatDuration } from '../lib/format';
import { layoutSection, type Tile } from '../lib/mosaic';
import { accentHex, onAccent } from '../lib/accent';
import { accents, colors } from '../styles/tokens';

/** Barras de color de cada sección (paleta de brands.mx). */
const SECTION_BARS = [
  accents.navy,
  accents.orange,
  accents.blue,
  accents.red,
  colors.inkSoft,
  accents.deepBlue,
];

/** Campos que son propuestas sin aprobar (solo en la vista previa con propuestas). */
const ProposalsContext = createContext<ReadonlySet<string> | null>(null);

/** Marca un texto si es una propuesta de Claude que el admin aún no aprueba. */
function Proposal({ field, children }: { field: string; children: ReactNode }) {
  const proposals = useContext(ProposalsContext);
  if (!proposals?.has(field)) return <>{children}</>;
  return (
    <mark className="pv-proposal" title="Propuesta de Claude sin aprobar">
      {children}
    </mark>
  );
}

/** Espacio bajo el encabezado y el índice (51px) donde se pega el panel de cada sección. */
const PANEL_GAP = 56;
const PANEL_BOTTOM = 16;

/**
 * Panel de sección pegajoso que siempre se puede leer completo ("sticky en dos direcciones").
 * Si cabe en la pantalla se queda fijo arriba (debajo del encabezado y el índice). Si es más alto,
 * se mueve junto con la página hasta mostrar su final al bajar, o su inicio al subir, y ahí se
 * queda fijo. Así cualquier parte del panel se lee con solo mover el scroll hacia ella.
 */
function useStickyFit() {
  const ref = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let current: number | null = null;
    let lastY = window.scrollY;
    let frame = 0;

    const limits = () => {
      const header =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) ||
        100;
      const max = header + PANEL_GAP;
      // Si el panel cabe, min == max: se queda fijo arriba.
      const min = Math.min(max, window.innerHeight - el.offsetHeight - PANEL_BOTTOM);
      return { min, max };
    };
    const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
    const apply = (v: number) => {
      current = v;
      el.style.setProperty('--sticky-top', `${Math.round(v)}px`);
    };

    const update = () => {
      frame = 0;
      const { min, max } = limits();
      const delta = window.scrollY - lastY;
      lastY = window.scrollY;
      if (current === null) return apply(max);
      const top = el.getBoundingClientRect().top;
      if (top > current + 1) {
        // Todavía no se pega (va en el flujo de la página): sigue su posición real para que al
        // pegarse continúe sin saltos.
        apply(clamp(top, min, max));
      } else if (Math.abs(top - current) <= 1) {
        // Pegado: se mueve con la página hasta mostrar su final (al bajar) o su inicio (al subir).
        apply(clamp(current - delta, min, max));
      } else {
        // Lo empuja el final de la sección; solo se ajusta a los límites.
        apply(clamp(current, min, max));
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);
  return ref;
}

const pad2 = (n: number) => String(n).padStart(2, '0');
/** "Fase 1 · Meta Ads" → "Meta Ads" (para espacios reducidos como el círculo). */
const shortTitle = (title: string) => title.split('·').pop()?.trim() ?? title;
const extOf = (name: string) =>
  name.includes('.') ? (name.split('.').pop()?.toUpperCase() ?? '') : '';

function Piece({
  campaignId,
  asset,
  tile,
  isNew,
  hasNote,
  onOpen,
}: {
  campaignId: string;
  asset: Asset;
  tile: Tile;
  isNew: boolean;
  hasNote: boolean;
  onOpen: () => void;
}) {
  const { ref, url, failed } = useLazyAssetUrl(
    campaignId,
    asset.id,
    asset.thumbLargePath ? tile.variant : 'thumb',
  );
  const meta =
    asset.kind === 'video' && asset.durationMs
      ? formatDuration(asset.durationMs)
      : asset.width && asset.height
        ? `${asset.width}×${asset.height}`
        : extOf(asset.name);
  return (
    <button
      ref={(el) => {
        ref.current = el;
      }}
      type="button"
      className={`mz mz-${tile.shape} mz--${tile.fit} ${asset.kind === 'video' ? 'mz--video' : ''} ${asset.kind === 'audio' ? 'mz--audio' : ''}`}
      onClick={onOpen}
      aria-label={`Ver ${asset.name}`}
    >
      {asset.thumbPath && url ? (
        <img className="mz__img" src={url} alt="" loading="lazy" decoding="async" />
      ) : (
        <span className="mz__placeholder t-meta">
          {asset.kind === 'audio' ? (
            <span className="mz__audio-icon" aria-hidden="true">
              ♪
            </span>
          ) : null}
          {failed || !asset.thumbPath
            ? `${asset.kind === 'audio' ? 'Audio · ' : ''}${extOf(asset.name) || 'Archivo'}`
            : ''}
        </span>
      )}
      {asset.kind === 'video' ? (
        <span className="mz__play" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" fill="currentColor" />
          </svg>
        </span>
      ) : null}
      {isNew ? <span className="badge-new mz__new">Nuevo</span> : null}
      {/* Barra inferior que crece en hover (como la project-bar de brands.mx) */}
      <span className="mz__bar">
        <span className="mz__name">{asset.name}</span>
        <span className="t-meta mz__meta">
          {meta}
          {hasNote ? ' · con nota' : ''}
        </span>
      </span>
    </button>
  );
}

function ZipLink({
  campaignId,
  sectionId,
  label,
}: {
  campaignId: string;
  sectionId?: string;
  label: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const { data } = await callables.createZipLink({
        campaignId,
        ...(sectionId ? { sectionId } : {}),
      });
      // Enlace de un solo uso; el servidor responde como descarga, la página no cambia.
      window.location.assign(data.url);
    } catch (e) {
      setError(callableMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="zip">
      <button
        type="button"
        className="bold-under zip__link"
        disabled={busy}
        onClick={() => void download()}
      >
        {busy ? 'Preparando descarga…' : label}
      </button>
      {error ? <span className="field__error zip__error">{error}</span> : null}
    </span>
  );
}

function Section({
  index,
  campaignId,
  section,
  assets,
  notes,
  isNew,
  onOpen,
}: {
  index: number;
  campaignId: string;
  section: LandingSection;
  assets: Asset[];
  notes: Map<string, string>;
  isNew: (a: Asset) => boolean;
  onOpen: (asset: Asset) => void;
}) {
  const tiles = useMemo(() => layoutSection(assets), [assets]);
  const panelRef = useStickyFit();
  const bytes = assets.reduce((s, a) => s + (a.size ?? 0), 0);
  const newCount = assets.filter(isNew).length;
  const bar = SECTION_BARS[index % SECTION_BARS.length];
  return (
    <section className="pj-section" id={section.id} aria-labelledby={`${section.id}-t`}>
      <aside
        ref={panelRef}
        className="pj-panel pj-panel--section"
        style={{ '--bar': bar } as CSSProperties}
      >
        <div className="pj-panel__inner">
          <p className="pj-num t-meta">{pad2(index + 1)}</p>
          <h2 id={`${section.id}-t`} className="t-title">
            {section.title}
          </h2>
          <p className="t-meta pj-muted">
            {assets.length} {assets.length === 1 ? 'pieza' : 'piezas'} · {formatBytes(bytes)}
            {newCount ? (
              <>
                {' '}
                ·{' '}
                <span className="badge-new">
                  {newCount === 1 ? '1 nueva' : `${newCount} nuevas`}
                </span>
              </>
            ) : null}
          </p>
          {section.description ? (
            <div className="pj-copy">
              <p className="bold-under">Qué incluye</p>
              <p className="t-text">
                <Proposal field={fieldKey.sectionDescription(section.id)}>
                  {section.description}
                </Proposal>
              </p>
            </div>
          ) : null}
          {section.justification ? (
            <div className="pj-copy">
              <p className="bold-under">Por qué</p>
              <p className="t-text">
                <Proposal field={fieldKey.sectionJustification(section.id)}>
                  {section.justification}
                </Proposal>
              </p>
            </div>
          ) : null}
          {assets.length > 1 ? (
            <ZipLink campaignId={campaignId} sectionId={section.id} label="Descargar sección" />
          ) : null}
        </div>
      </aside>
      <div className="pj-mosaic-wrap">
        <div className="pj-mosaic">
          {assets.map((a, i) => (
            <Piece
              key={a.id}
              campaignId={campaignId}
              asset={a}
              tile={tiles[i] ?? { shape: 'sq', fit: 'full', variant: 'thumb' }}
              isNew={isNew(a)}
              hasNote={notes.has(a.id)}
              onOpen={() => onOpen(a)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function HeroImage({ campaignId, asset }: { campaignId: string; asset: Asset }) {
  const { ref, url } = useLazyAssetUrl(
    campaignId,
    asset.id,
    asset.thumbLargePath ? 'large' : 'thumb',
  );
  return (
    <div
      ref={(el) => {
        ref.current = el;
      }}
      className="pj-hero__media"
    >
      {url ? <img src={url} alt="" /> : null}
    </div>
  );
}

export function CampaignPage({ campaignId }: { campaignId?: string } = {}) {
  const params = useParams();
  const id = campaignId ?? params.id;
  const campaign = useCampaign(id);
  const { session } = useAuth();
  const [search] = useSearchParams();
  // Vista previa del borrador (solo admins): ?vista=borrador, con &propuestas=1 para incluir las
  // propuestas sin aprobar marcadas. Sin ese parámetro se muestra exactamente lo que se publicaría.
  const preview = Boolean(session?.isStaff) && search.get('vista') === 'borrador';
  const withProposals = preview && search.get('propuestas') === '1';
  const source = useLandingContent(id, preview ? 'draft' : 'published');
  const landing = useMemo(() => {
    if (!preview || withProposals || source.status !== 'ready' || campaign.status !== 'ready')
      return source;
    return { ...source, data: stripProposals(source.data, campaign.data.name) };
  }, [preview, withProposals, source, campaign]);
  const proposals = useMemo(
    () => (withProposals && source.status === 'ready' ? new Set(source.data.proposals) : null),
    [withProposals, source],
  );
  const deliverables = useDeliverables(id);
  const lastVisit = useLastVisit(id);
  const [open, setOpen] = useState<number | null>(null);

  const isNew = useCallback((a: Asset) => isNewSince(a, lastVisit), [lastVisit]);

  // Piezas por sección, en el orden de la landing; las que no estén en ninguna van al final.
  const { sections, ordered } = useMemo(() => {
    if (landing.status !== 'ready' || deliverables.status !== 'ready')
      return { sections: [], ordered: [] };
    const map = deliverables.data;
    const seen = new Set<string>();
    const result = landing.data.sections
      .map((s) => {
        const assets = s.assetIds.flatMap((aid) => {
          const a = map.get(aid);
          if (!a || seen.has(aid)) return [];
          seen.add(aid);
          return [a];
        });
        return { section: s, assets };
      })
      .filter((s) => s.assets.length > 0);
    const rest = [...map.values()].filter((a) => !seen.has(a.id));
    if (rest.length) {
      result.push({
        section: {
          id: 'otros',
          title: 'Otros entregables',
          description: null,
          justification: null,
          sourceDocs: [],
          folderPaths: [],
          assetIds: rest.map((a) => a.id),
        },
        assets: rest,
      });
    }
    return { sections: result, ordered: result.flatMap((s) => s.assets) };
  }, [landing, deliverables]);

  const notes = useMemo(
    () =>
      new Map(
        landing.status === 'ready'
          ? landing.data.pieceNotes.map((n) => [n.assetId, n.justification])
          : [],
      ),
    [landing],
  );

  if (
    campaign.status === 'loading' ||
    landing.status === 'loading' ||
    deliverables.status === 'loading'
  ) {
    return <Loading />;
  }
  if (campaign.status !== 'ready' || landing.status !== 'ready' || !id) {
    return (
      <main className="page page__pad">
        <h1 className="t-display">No disponible</h1>
        <p className="t-lead empty__text">
          {preview
            ? 'Esta campaña todavía no tiene borrador. Genera la landing desde el panel.'
            : 'Esta campaña no existe o todavía no está publicada.'}
        </p>
        <Link to="/campanas" className="btn-link">
          Volver a campañas
        </Link>
      </main>
    );
  }

  const c = campaign.data;
  const content = landing.data;
  const accent = accentHex(c.accent);
  const textOnAccent = onAccent(c.accent);
  const totalBytes = ordered.reduce((s, a) => s + (a.size ?? 0), 0);
  const newTotal = ordered.filter(isNew).length;
  const chosen =
    content.heroAssetId && deliverables.status === 'ready'
      ? deliverables.data.get(content.heroAssetId)
      : undefined;
  const heroAsset =
    chosen ??
    ordered.find(
      (a) => a.kind === 'image' && a.thumbLargePath && (a.width ?? 1) >= (a.height ?? 1),
    ) ??
    ordered.find((a) => a.thumbLargePath);

  const pendingCount = source.status === 'ready' ? source.data.proposals.length : 0;
  return (
    <ProposalsContext.Provider value={proposals}>
      {preview ? (
        <div className="pv-bar" role="status">
          <p className="t-nav">Vista previa · sin publicar</p>
          <p className="t-meta">
            {withProposals
              ? `Incluye ${pendingCount} propuestas de Claude sin aprobar, marcadas con borde rojo.`
              : pendingCount
                ? `Así se publicaría ahora: ${pendingCount} propuestas sin aprobar no aparecen.`
                : 'Así se publicaría ahora.'}{' '}
            Muestra el borrador guardado.
          </p>
          <div className="pv-bar__actions">
            {pendingCount ? (
              <Link
                to={`?vista=borrador${withProposals ? '' : '&propuestas=1'}`}
                replace
                className="btn-link"
              >
                {withProposals ? 'Ver sin propuestas' : 'Ver con propuestas'}
              </Link>
            ) : null}
            <Link to={`/admin/c/${id}`} className="btn-link">
              Volver al editor
            </Link>
          </div>
        </div>
      ) : null}
      <main
        className="pj"
        style={{ '--accent': accent, '--on-accent': textOnAccent } as CSSProperties}
      >
        {/* Portada de proyecto: panel de información + pieza clave + círculo con el índice */}
        <header className="pj-hero">
          <div className="pj-panel pj-panel--hero">
            <div className="pj-panel__inner">
              {c.brandId && c.clientSlug ? (
                <Link
                  to={brandPath(window.location.host, c.clientSlug)}
                  className="t-nav pj-muted link-nav"
                >
                  ← {c.clientName}
                </Link>
              ) : (
                <p className="t-nav pj-muted">{c.clientName}</p>
              )}
              <h1 className="pj-title">
                <Proposal field={fieldKey.heroTitle}>{content.hero.title}</Proposal>
              </h1>
              {content.hero.subtitle ? (
                <p className="bold-under pj-claim">
                  <Proposal field={fieldKey.heroSubtitle}>{content.hero.subtitle}</Proposal>
                </p>
              ) : null}
              {content.hero.summary ? (
                <p className="t-text">
                  <Proposal field={fieldKey.heroSummary}>{content.hero.summary}</Proposal>
                </p>
              ) : null}
              {content.concept.body ? (
                <div className="pj-copy">
                  <p className="bold-under">
                    {content.concept.title && content.concept.title !== content.hero.subtitle
                      ? content.concept.title
                      : 'Concepto'}
                  </p>
                  <p className="t-text">
                    <Proposal field={fieldKey.conceptBody}>{content.concept.body}</Proposal>
                  </p>
                </div>
              ) : null}
              {content.objective ? (
                <div className="pj-copy">
                  <p className="bold-under">Objetivo</p>
                  <p className="t-text">
                    <Proposal field={fieldKey.objective}>{content.objective}</Proposal>
                  </p>
                </div>
              ) : null}
              <div className="pj-stats">
                <p className="t-meta pj-muted">
                  {ordered.length} piezas · {sections.length} secciones · {formatBytes(totalBytes)}
                </p>
                {newTotal ? (
                  <p>
                    <span className="badge-new">
                      {newTotal === 1 ? '1 pieza nueva' : `${newTotal} piezas nuevas`} desde tu
                      última visita
                    </span>
                  </p>
                ) : null}
                <ZipLink campaignId={id} label="Descargar todo" />
              </div>
            </div>
          </div>

          <div className="pj-hero__visual">
            {heroAsset ? (
              <HeroImage campaignId={id} asset={heroAsset} />
            ) : (
              <div className="pj-hero__media pj-hero__media--color" />
            )}
            <nav className="pj-circle t-text" aria-label="Secciones">
              {sections.slice(0, 6).map(({ section }, i) => (
                <a key={section.id} href={`#${section.id}`} title={section.title}>
                  {pad2(i + 1)} {shortTitle(section.title)}
                </a>
              ))}
              {sections.length > 6 ? <a href="#indice">—&nbsp;Ver todas</a> : null}
            </nav>
          </div>
        </header>

        <nav id="indice" className="pj-index t-nav" aria-label="Índice de secciones">
          {sections.map(({ section }, i) => (
            <a key={section.id} href={`#${section.id}`} className="link-nav">
              {pad2(i + 1)} {section.title}
            </a>
          ))}
        </nav>

        {sections.map(({ section, assets }, i) => (
          <Section
            key={section.id}
            index={i}
            campaignId={id}
            section={section}
            assets={assets}
            notes={notes}
            isNew={isNew}
            onOpen={(a) => setOpen(ordered.findIndex((x) => x.id === a.id))}
          />
        ))}

        <footer className="pj-footer">
          <p className="t-title">{content.hero.title}</p>
          <ZipLink campaignId={id} label={`Descargar todo · ${formatBytes(totalBytes)}`} />
          <p className="t-meta pj-muted">Brands&amp;People</p>
        </footer>

        {open !== null ? (
          <Lightbox
            campaignId={id}
            items={ordered}
            index={open}
            notes={notes}
            isNew={isNew}
            onIndex={setOpen}
            onClose={() => setOpen(null)}
          />
        ) : null}
      </main>
    </ProposalsContext.Provider>
  );
}
